<template>
  <div class="draggable-window" :style="style" @pointerdown.stop="onRootPointerDown">
    <div class="dw-header" ref="headerEl" @pointerdown="startDrag">
      <div class="dw-title">
        <slot name="header-left">
          <div class="dw-title-text">{{ title }}</div>
        </slot>
      </div>
      <div class="dw-controls"><slot name="controls"></slot></div>
    </div>
    <div class="dw-body" :class="{ 'dw-body--hidden': props.hideBody }" :style="bodyStyle">
      <slot name="body"></slot>
    </div>
    <div class="dw-footer">
      <slot name="footer"></slot>
    </div>
    <div
      v-if="resizable && !props.hideBody"
      class="dw-resize-handle dw-resize-handle--br"
      @pointerdown.prevent="(e) => startResize(e, 'br')"
    />
    <div
      v-if="resizable && !props.hideBody"
      class="dw-resize-handle dw-resize-handle--bl"
      @pointerdown.prevent="(e) => startResize(e, 'bl')"
    />
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'

  const props = defineProps({
    title: { type: String, default: '' },
    initialX: { type: Number, default: 40 },
    initialY: { type: Number, default: 40 },
    // z-index for stacking order, controlled by parent
    zIndex: { type: Number, default: 10 },
    // Body area size (below header)
    initialWidth: { type: Number, default: 300 },
    initialHeight: { type: Number, default: 200 },
    minWidth: { type: Number, default: 220 },
    minHeight: { type: Number, default: 140 },
    resizable: { type: Boolean, default: true },
    // Screen margin when clamping to viewport
    viewportMargin: { type: Number, default: 8 },
    // Optional explicit content height override (px); when null, uses internal height
    contentHeight: { type: Number, default: null },
    // Force-hide the body content entirely (display: none)
    hideBody: { type: Boolean, default: false }
  })

  const emit = defineEmits(['focus'])

  const x = ref(props.initialX)
  const y = ref(props.initialY)
  const dragging = ref(false)
  const startX = ref(0)
  const startY = ref(0)
  const w = ref(props.initialWidth)
  const h = ref(props.initialHeight)
  const headerEl = ref(null)
  const activePointerId = ref(null)

  const resizing = ref(false)
  const startW = ref(0)
  const startH = ref(0)
  const startClientX = ref(0)
  const startClientY = ref(0)
  const startXPos = ref(0)
  const resizeDir = ref('br')
  const resizePointerId = ref(null)

  const style = computed(() => ({
    transform: `translate3d(${x.value}px, ${y.value}px, 0)`,
    position: 'fixed',
    width: `${w.value}px`,
    zIndex: props.zIndex
  }))

  const bodyStyle = computed(() => {
    const hh = effectiveBodyHeight()
    return { height: `${Math.max(0, Number(hh))}px` }
  })

  function effectiveBodyHeight() {
    if (props.hideBody === true) return 0
    return props.contentHeight === null ? h.value : props.contentHeight
  }

  function isInteractiveTarget(el) {
    try {
      if (!el) return false
      const sel =
        '.no-drag, .q-field, .q-select, .q-btn, .q-menu, input, select, textarea, button, a, [role="button"], [role="listbox"], [contenteditable=""], [contenteditable="true"]'
      return !!el.closest(sel)
    } catch {
      return false
    }
  }

  function startDrag(e) {
    if (isInteractiveTarget(e.target)) {
      return
    }
    try {
      emit('focus')
    } catch {}
    dragging.value = true
    try {
      activePointerId.value = e.pointerId || null
      headerEl.value?.setPointerCapture?.(e.pointerId)
    } catch {}
    try {
      e.preventDefault()
    } catch {}
    startX.value = e.clientX - x.value
    startY.value = e.clientY - y.value
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', stopDrag)
  }

  function onRootPointerDown() {
    try {
      emit('focus')
    } catch {}
  }

  function onPointerMove(e) {
    if (!dragging.value) return
    const margin = props.viewportMargin
    const headerH = headerEl.value ? headerEl.value.offsetHeight : 32
    const maxX = Math.max(margin, window.innerWidth - w.value - margin)
    const effH = Math.max(0, Number(effectiveBodyHeight()))
    const maxY = Math.max(margin, window.innerHeight - (headerH + effH) - margin)
    x.value = clamp(e.clientX - startX.value, margin, maxX)
    y.value = clamp(e.clientY - startY.value, margin, maxY)
  }

  function stopDrag() {
    dragging.value = false
    try {
      if (activePointerId.value != null) {
        headerEl.value?.releasePointerCapture?.(activePointerId.value)
      }
    } catch {}
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', stopDrag)
  }

  function startResize(e, dir = 'br') {
    resizing.value = true
    resizeDir.value = dir === 'bl' ? 'bl' : 'br'
    startW.value = w.value
    startH.value = h.value
    startClientX.value = e.clientX
    startClientY.value = e.clientY
    startXPos.value = x.value
    try {
      resizePointerId.value = e.pointerId || null
      ;(e.currentTarget || null)?.setPointerCapture?.(e.pointerId)
    } catch {}
    try {
      e.preventDefault()
    } catch {}
    window.addEventListener('pointermove', onResizeMove)
    window.addEventListener('pointerup', stopResize)
  }

  function onResizeMove(e) {
    if (!resizing.value) return
    const dx = e.clientX - startClientX.value
    const dy = e.clientY - startClientY.value
    const margin = props.viewportMargin
    const headerH = headerEl.value ? headerEl.value.offsetHeight : 32
    const maxH = Math.max(props.minHeight, window.innerHeight - y.value - headerH - margin)

    if (resizeDir.value === 'br') {
      const maxW = Math.max(props.minWidth, window.innerWidth - x.value - margin)
      w.value = clamp(startW.value + dx, props.minWidth, maxW)
    } else {
      // bottom-left: move left edge, update x and width from fixed right edge
      const rightEdge = startXPos.value + startW.value
      const xMin = margin
      const xMax = Math.max(margin, rightEdge - props.minWidth)
      const nextX = clamp(startXPos.value + dx, xMin, xMax)
      const nextW = rightEdge - nextX
      x.value = nextX
      w.value = clamp(nextW, props.minWidth, rightEdge - xMin)
    }
    h.value = clamp(startH.value + dy, props.minHeight, maxH)
  }

  function stopResize(e) {
    resizing.value = false
    try {
      if (resizePointerId.value != null) {
        ;(e?.currentTarget || null)?.releasePointerCapture?.(resizePointerId.value)
      }
    } catch {}
    window.removeEventListener('pointermove', onResizeMove)
    window.removeEventListener('pointerup', stopResize)
  }

  function clamp(val, min, max) {
    if (Number.isNaN(val)) return min
    if (min > max) return min
    return Math.min(Math.max(val, min), max)
  }

  function clampToViewport() {
    try {
      const margin = props.viewportMargin
      const headerH = headerEl.value ? headerEl.value.offsetHeight : 32
      // Clamp size first to ensure it fits
      const maxW = Math.max(props.minWidth, window.innerWidth - margin)
      const maxH = Math.max(props.minHeight, window.innerHeight - headerH - margin)
      w.value = clamp(w.value, props.minWidth, maxW)
      h.value = clamp(h.value, props.minHeight, maxH)
      // Then clamp position
      const maxX = Math.max(margin, window.innerWidth - w.value - margin)
      const effH = Math.max(0, Number(effectiveBodyHeight()))
      const maxY = Math.max(margin, window.innerHeight - (headerH + effH) - margin)
      x.value = clamp(x.value, margin, maxX)
      y.value = clamp(y.value, margin, maxY)
    } catch {}
  }

  function onWindowResize() {
    clampToViewport()
  }

  onMounted(() => {
    window.addEventListener('resize', onWindowResize)
    // Initial clamp in case defaults exceed viewport
    clampToViewport()
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', onWindowResize)
  })

  // Re-clamp when hideBody/contentHeight toggles to avoid leftover constraints
  watch(
    () => props.hideBody,
    () => clampToViewport()
  )
  watch(
    () => props.contentHeight,
    () => clampToViewport()
  )
</script>

<style scoped>
  .draggable-window {
    background: rgba(20, 20, 20, 0.95);
    color: #fff;
    border-radius: 10px;
    overflow: hidden;
    pointer-events: auto;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
    position: fixed;
    top: 0;
    left: 0;
    /* Force isolation to avoid other stacking contexts interfering */
    isolation: isolate;
  }
  .dw-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 8px;
    cursor: grab;
    /* Prevent mobile page panning/zooming while dragging (interactive children can override) */
    touch-action: none;
  }
  .dw-header .no-drag {
    touch-action: auto;
  }
  .dw-title {
    font-weight: 600;
    font-size: 13px;
  }
  .dw-controls {
    display: flex;
    gap: 6px;
    align-items: center;
  }
  .dw-footer {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
  }
  .dw-body {
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .dw-resize-handle {
    position: absolute;
    width: 20px;
    height: 20px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.35), rgba(255, 255, 255, 0.05));
    border-radius: 3px;
    opacity: 0.65;
    touch-action: none;
  }
  .dw-resize-handle--br {
    right: 2px;
    bottom: 2px;
    cursor: nwse-resize;
  }
  .dw-resize-handle--bl {
    left: 2px;
    bottom: 2px;
    cursor: nesw-resize;
  }
  .dw-resize-handle:hover {
    opacity: 0.9;
  }
  .dw-body--hidden {
    /* Keep DOM active for media decoding; visually collapse */
    opacity: 0;
    visibility: hidden;
    height: 0 !important;
    min-height: 0 !important;
    overflow: hidden !important;
    pointer-events: none;
  }
</style>
